import React, { useCallback, useEffect,useMemo, useState } from 'react';
import { DocumentDetailModal } from '@features/documents';
import {
  useLazyGetDocumentDownloadUrlQuery,
  usePatientDocuments,
} from '@features/patient-documents';
import { useSubjectLanguageVariants } from '@features/user';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ScienceIcon from '@mui/icons-material/Science';
import SummarizeIcon from '@mui/icons-material/Summarize';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig/hooks';
import type { DocumentSummaryResponse } from '@store/generated/patients';
import { useSnackbar } from 'notistack';

import { capitalize, formatDateString } from '../../../utils/utils';

import CollapsibleBaseBlock from './CollapsibleBaseBlock';

interface PatientDocumentsBlockProps {
  patientId: string;
}

// Document type display mapping
const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  lab_order: 'Lab Order',
  claim: 'Claim',
  form: 'Form',
  report: 'Report',
  image: 'Image',
  other: 'Other',
};

// Document type icon and color mapping
const DOCUMENT_TYPE_CONFIG: Record<string, { icon: React.ReactNode; color: string }> = {
  lab_order: { icon: <ScienceIcon fontSize="small" />, color: '#2196f3' }, // blue
  claim: { icon: <ReceiptLongIcon fontSize="small" />, color: '#ff9800' }, // orange
  form: { icon: <AssignmentIcon fontSize="small" />, color: '#9c27b0' }, // purple
  report: { icon: <SummarizeIcon fontSize="small" />, color: '#4caf50' }, // green
  image: { icon: <ImageIcon fontSize="small" />, color: '#f44336' }, // red
  other: { icon: <DescriptionIcon fontSize="small" />, color: '#757575' }, // gray
};

const PatientDocumentsBlock: React.FC<PatientDocumentsBlockProps> = ({ patientId }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [documentTypeFilter, setDocumentTypeFilter] = useState<string>('');
  const subjectLanguageVariant = useSubjectLanguageVariants();
  const { enqueueSnackbar } = useSnackbar();
  const [triggerDownload] = useLazyGetDocumentDownloadUrlQuery();
  const { document: documentMetrics } = useEventTracking();
  const [hasTrackedInitialLoad, setHasTrackedInitialLoad] = useState(false);
  
  // Document detail modal state
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Fetch patient documents with RTK Query
  const { data, total, isLoading, isError, error } = usePatientDocuments({
    patientId,
    documentType: documentTypeFilter || undefined,
    includeDeleted: false,
    page: page + 1, // API uses 1-based pages
    perPage: rowsPerPage,
  });

  // Get unique document types from data for filter dropdown
  const availableDocumentTypes = useMemo(() => {
    if (!data) return [];
    const types = Array.from(
      new Set(data.map(doc => doc.document_type).filter((type): type is string => Boolean(type))),
    );
    return types.sort();
  }, [data]);
  
  // Track initial load
  useEffect(() => {
    if (!isLoading && !hasTrackedInitialLoad && data) {
      documentMetrics.listLoaded({
        total_documents: total,
        document_types: availableDocumentTypes.length > 0 ? availableDocumentTypes : undefined,
      });
      setHasTrackedInitialLoad(true);
    }
  }, [isLoading, hasTrackedInitialLoad, data, total, availableDocumentTypes, documentMetrics]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    
    // Track pagination change
    documentMetrics.paginationChanged({
      new_page: newPage,
      rows_per_page: rowsPerPage,
      total_pages: Math.ceil(total / rowsPerPage),
    });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    
    // Track pagination change
    documentMetrics.paginationChanged({
      new_page: 0,
      rows_per_page: newRowsPerPage,
      total_pages: Math.ceil(total / newRowsPerPage),
    });
  };

  const handleDocumentTypeChange = (event: SelectChangeEvent) => {
    const filterType = event.target.value;
    setDocumentTypeFilter(filterType);
    setPage(0); // Reset to first page when filtering
    
    // Track filter event
    if (data) {
      documentMetrics.filtered({
        filter_type: filterType || 'all',
        documents_shown: data.length,
        total_documents: total,
      });
    }
  };

  const handleOpenDocument = useCallback((document: DocumentSummaryResponse) => {
    // Track document view event
    documentMetrics.viewed(document.document_id, {
      document_type: document.document_type || undefined,
      patient_id: patientId,
    });
    
    // Open in document detail modal
    setSelectedDocumentId(document.document_id);
    setIsDetailModalOpen(true);
  }, [documentMetrics, patientId]);

  const handleCloseModal = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedDocumentId(null);
  }, []);

  const handleDownloadDocument = async (e: React.MouseEvent, document: DocumentSummaryResponse) => {
    e.stopPropagation(); // Prevent row click
    try {
      const response = await triggerDownload({ documentId: document.document_id }).unwrap();
      // For cross-origin URLs (like S3), we can't force download with the download attribute
      // Opening in a new window/tab will trigger the browser's download if the content-disposition header is set
      window.open(response.download_url, '_blank');
    } catch (error) {
      console.error('Failed to download document:', error);
      enqueueSnackbar('Failed to download document', { variant: 'error' });
    }
  };

  const renderDocumentTypeIcon = (documentType: string | null) => {
    if (!documentType) {
      return (
        <Box sx={{ color: '#757575' }}>
          <DescriptionIcon fontSize="small" />
        </Box>
      );
    }

    const config = DOCUMENT_TYPE_CONFIG[documentType] || DOCUMENT_TYPE_CONFIG.other;
    const label = DOCUMENT_TYPE_LABELS[documentType] || capitalize(documentType);

    return (
      <Tooltip title={label}>
        <Box sx={{ color: config.color, display: 'flex', alignItems: 'center' }}>
          {config.icon}
        </Box>
      </Tooltip>
    );
  };

  const renderSkeletonRows = () => {
    return Array.from({ length: rowsPerPage }, (_, index) => (
      <TableRow key={index}>
        <TableCell padding="checkbox">
          <Skeleton variant="circular" width={20} height={20} />
        </TableCell>
        <TableCell>
          <Skeleton width="60%" height={20} />
        </TableCell>
        <TableCell>
          <Skeleton width="40%" height={20} />
        </TableCell>
        <TableCell>
          <Skeleton width="30%" height={20} />
        </TableCell>
      </TableRow>
    ));
  };

  const subtitle = useMemo(() => {
    if (isLoading) return null;
    if (total === 0) return 'No documents found';

    const filtered = documentTypeFilter ? ` (${capitalize(documentTypeFilter)} only)` : '';
    return `${total} document${total !== 1 ? 's' : ''}${filtered}`;
  }, [total, isLoading, documentTypeFilter]);

  return (
    <>
      <CollapsibleBaseBlock defaultCollapsed={false}>
        <CollapsibleBaseBlock.Header
          icon={<DescriptionIcon />}
          title={`${subjectLanguageVariant.singularCap} Documents`}
          subtitle={subtitle}
          loading={isLoading}
          action={
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Filter by Type</InputLabel>
              <Select
                value={documentTypeFilter}
                label="Filter by Type"
                onChange={handleDocumentTypeChange}
                disabled={isLoading}
              >
                <MenuItem value="">
                  <em>All Types</em>
                </MenuItem>
                {availableDocumentTypes.map(type => (
                  <MenuItem key={type} value={type}>
                    {DOCUMENT_TYPE_LABELS[type] || capitalize(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          }
        />

      <CollapsibleBaseBlock.Body>
        {isError && (
          <Box sx={{ p: 2, color: 'error.main' }}>
            <Typography variant="body2">
              Error loading documents: {error ? 'Failed to load documents' : 'Unknown error'}
            </Typography>
          </Box>
        )}

        {!isError && (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '5%' }} />
                  <TableCell sx={{ width: '50%' }}>Title</TableCell>
                  <TableCell sx={{ width: '30%' }}>Created</TableCell>
                  <TableCell sx={{ width: '15%' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  renderSkeletonRows()
                ) : data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No documents found
                        {documentTypeFilter &&
                          ` for type "${DOCUMENT_TYPE_LABELS[documentTypeFilter] || documentTypeFilter}"`}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map(document => {
                    const title = document.document_title || 'Untitled Document';
                    const isUntitled = !document.document_title;
                    const truncatedTitle = title.length > 20 ? `${title.substring(0, 20)}...` : title;
                    const showTooltip = title.length > 20;
                    
                    return (
                      <Tooltip 
                        key={document.document_id} 
                        title={showTooltip ? title : ''} 
                        enterDelay={500} 
                        placement="top-start"
                        disableHoverListener={!showTooltip}
                      >
                        <TableRow 
                          hover 
                          onClick={() => handleOpenDocument(document)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            {renderDocumentTypeIcon(document.document_type || null)}
                          </TableCell>
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: isUntitled ? 'text.secondary' : 'text.primary',
                                fontStyle: isUntitled ? 'italic' : 'normal'
                              }}
                            >
                              {truncatedTitle}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDateString(document.created_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="View document">
                                <IconButton 
                                  size="small" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenDocument(document);
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download document">
                                <IconButton size="small" onClick={(e) => handleDownloadDocument(e, document)}>
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      </Tooltip>
                    );
                  })
                )}
              </TableBody>
            </Table>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </CollapsibleBaseBlock.Body>
    </CollapsibleBaseBlock>

      {/* Document Detail Modal */}
      <DocumentDetailModal
        open={isDetailModalOpen}
        documentId={selectedDocumentId}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default PatientDocumentsBlock;
